import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faTimes } from '@fortawesome/free-solid-svg-icons';

import VisibilityIcon from '@mui/icons-material/Visibility';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import LwtTable from '@lwtears/lwt-common-frontend/lib/Table';
import { translate } from 'translations/translator';
import { PopoverChip } from 'components/PopoverChip';
import LessonPlannerTranslationVariant from 'translations/variants/lessonPlanner';
import { TableColumnProps } from '@lwtears/lwt-common-frontend/lib/Table';

const getTranslation = (key: LessonPlannerTranslationVariant | string) =>
  translate('lessonPlanner', key);
const { manageInActiveChip, manageActiveChip, notInUse, currentlyInUse } =
  LessonPlannerTranslationVariant;
export type Props = {
  teachingOrders: Array<{
    id: number;
    name: string;
    default: boolean;
    custom: boolean;
    active: boolean;
  }>;
  getTeachingOrderActions: (custom: boolean) => TeachingOrderActions;
};
type TeachingOrderActions = {
  onRenameAction: ({ id, custom }) => void;
  onDeleteAction: ({ id, custom }) => void;
  onUseAction: ({ id, custom }) => void;
  onViewAction: ({ id, custom }) => void;
};
export const ManageTeachingOrderOptions: React.FC<Props> = ({
  teachingOrders,
  getTeachingOrderActions
}: Props) => {
  const classes = useStyles()();

  const getPopOverBodyTranslation = active =>
    active ? getTranslation(manageActiveChip) : getTranslation(manageInActiveChip);

  const getStatusTranslation = (active): any =>
    active ? getTranslation(currentlyInUse) : getTranslation(notInUse);

  const columns: TableColumnProps<any>[] = [
    {
      label: (
        <Typography variant="caption">
          {getTranslation(LessonPlannerTranslationVariant.teachingOrderName)}
        </Typography>
      ),
      getValue(item): React.ReactElement {
        return (
          <div key={`${item.id}_col_1`} style={{ display: 'flex' }}>
            <Typography variant="body2">{item.name}</Typography>
          </div>
        );
      }
    },
    {
      label: (
        <Typography variant="caption">
          {getTranslation(LessonPlannerTranslationVariant.actions)}
        </Typography>
      ),
      getValue(item): React.ReactElement {
        return (
          <div
            style={{
              display: 'flex',
              whiteSpace: 'nowrap',
              marginRight: 5,
              width: '100%',
              alignItems: 'center'
            }}
            key={`${item.id}_col_2`}
          >
            {item.active && (
              <PopoverChip
                style={{
                  width: 'max-content !important'
                }}
                color={item.active ? '#2878C1' : '#A6A6A6'}
                body={getPopOverBodyTranslation(item.active)}
                status={getStatusTranslation(item.active)}
              />
            )}
            {item.actions.map((x, idx) => (
              <RowActionIcon
                key={x + idx}
                action={x}
                classes={classes}
                teachingOrderActions={getTeachingOrderActions(item.custom)}
                id={item.data.id}
                custom={item.custom}
                active={item.active}
              />
            ))}
          </div>
        );
      }
    }
  ];

  const data = teachingOrders.map(x => ({
    id: x.id,
    name: x.name,
    actions: Object.keys(getTeachingOrderActions(x.custom)),
    custom: x?.custom,
    data: x,
    active: x.active
  }));

  return (
    <Container>
      <Typography variant="h4" style={{ marginTop: '32px', marginBottom: '10px', fontWeight: 400 }}>
        {getTranslation('manageTeachingOrder')}
      </Typography>
      <Box style={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
        <Typography variant="h6" style={{ marginBottom: '32px', fontWeight: 300 }}>
          {getTranslation(LessonPlannerTranslationVariant.viewAndManageTeachingOrderModalTitle)}
        </Typography>
        <Box style={{ marginTop: '-25px' }}>
          <LwtTable
            columns={columns}
            data={data}
            options={{ groupIndexes: [1] }}
            classes={{
              tableContainer: (): string => classes.table,
              table: (): string => `${classes.table} ${classes.tableExt}`
            }}
            rowProps={() => ({
              style: {
                height: 'max-content',
                width: '100%'
              }
            })}
          />
        </Box>
      </Box>
    </Container>
  );
};

const RowActionIcon: React.FC<{
  action: string;
  classes: any;
  teachingOrderActions: TeachingOrderActions;
  id: any;
  custom: boolean;
  active: boolean;
}> = ({ action, classes, teachingOrderActions, id, custom, active }) => {
  switch (action) {
    case 'onDeleteAction':
      return (
        <Button
          className={classes.rowActionIconButton}
          onClick={() => teachingOrderActions?.onDeleteAction({ id, custom })}
        >
          <FontAwesomeIcon icon={faTimes} className={classes.buttonIcon} />
          <span className={classes.buttonText}>
            {getTranslation(LessonPlannerTranslationVariant.delete)}
          </span>
        </Button>
      );
    case 'onRenameAction':
      return (
        <Button
          className={classes.rowActionIconButton}
          onClick={() => teachingOrderActions?.onRenameAction({ id, custom })}
        >
          <InsertDriveFileIcon className={classes.buttonIcon} />
          <span className={classes.buttonText}>
            {getTranslation(LessonPlannerTranslationVariant.rename)}
          </span>
        </Button>
      );
    case 'onUseAction':
      return !active ? (
        <Button
          className={classes.rowActionIconButton}
          onClick={() => teachingOrderActions?.onUseAction({ id, custom })}
          disabled={active}
        >
          <FontAwesomeIcon icon={faArrowRight} className={classes.buttonIcon} />
          <span className={classes.buttonText}>
            {getTranslation(LessonPlannerTranslationVariant.useThisOrder)}
          </span>
        </Button>
      ) : null;
    case 'onViewAction':
      return !active ? (
        <Button
          className={classes.rowActionIconButton}
          onClick={() => teachingOrderActions.onViewAction({ id, custom })}
        >
          <VisibilityIcon className={classes.buttonIcon} />
          <span className={classes.buttonText}>
            {getTranslation(LessonPlannerTranslationVariant.preview)}
          </span>
        </Button>
      ) : null;
    default:
      return (
        <Button
          className={classes.rowActionIconButton}
          disabled
          style={{ border: 'none' }}
        ></Button>
      );
  }
};
const useStyles = () =>
  makeStyles(() => ({
    container: {
      display: 'flex',
      flexDirection: 'column',
      marginTop: '22px',
      padding: '24px',
      height: '445px',
      maxWidth: '1256px'
    },
    button: {
      width: '106px'
    },
    buttonText: {
      fontSize: '14px !important',
      margin: '0 5px 0 0'
    },
    buttonIcon: {
      fontSize: '16px !important',
      margin: '0px 5px'
    },
    tableHeader: {
      display: 'flex',
      flex: 0.51,
      justifyContent: 'space-between',
      margin: '5px 0px'
    },
    table: {
      maxHeight: '400px',
      overflow: 'overlay',
      '& .MuiTableCell-body': {
        width: '50%'
      }
    },
    tableExt: {
      overflow: 'hidden !important'
    },
    rowActionIconButton: {
      paddingLeft: '0 !important',
      width: 'max-content !important',
      height: '37px',
      textTransform: 'none !important',
      margin: '0px 20px 0 0 !important'
    } as any
  }));
